import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { lstr } from "./localization";
import { fetchPlacementWords, submitPlacement } from "./practiceApi";
import { Pup } from "./Pup";
import { SkeletonBlock } from "./Skeleton";

// PlacementOffer invites a new learner to the placement game before their
// first practice words, which then come from the right level.
export function PlacementOffer({
  l,
  r,
  onStart,
  onSkip,
}: {
  l: string;
  r: string;
  onStart: () => void;
  onSkip: () => void;
}) {
  const strings = lstr(l);
  const [skipping, setSkipping] = useState(false);
  const [failed, setFailed] = useState(false);
  const skip = () => {
    setSkipping(true);
    submitPlacement(l, r, "skip")
      .then(onSkip)
      .catch(() => {
        setFailed(true);
        setSkipping(false);
      });
  };
  return (
    <div className="card animate-pop-in flex w-full flex-col items-center gap-3 rounded-3xl px-6 py-9 text-center">
      <Pup shape="circle" className="h-20 w-20" />
      <h2 className="mt-2 font-literata text-2xl font-bold text-main-text">
        {strings.placement_title}
      </h2>
      <p className="max-w-md text-main-text">{strings.placement_text}</p>
      <div className="mt-3 flex flex-wrap justify-center gap-2">
        <button
          type="button"
          onClick={onStart}
          className="btn-primary px-6 py-3"
        >
          {strings.placement_start}
        </button>
        <button
          type="button"
          disabled={skipping}
          onClick={skip}
          className="btn-secondary px-5 py-3"
        >
          {strings.placement_skip}
        </button>
      </div>
      {failed && <p className="text-sm text-error">{strings.practice_error}</p>}
    </div>
  );
}

// PlacementGame shows one word at a time; the learner says whether they know
// it. Some words are made up, which keeps the estimate honest.
export function PlacementGame({
  l,
  r,
  onDone,
}: {
  l: string;
  r: string;
  onDone: () => void;
}) {
  const strings = lstr(l);
  const words = useQuery({
    queryKey: ["placement-words", r],
    queryFn: () => fetchPlacementWords(r),
    staleTime: Infinity,
    gcTime: 0,
    retry: 1,
  });
  const [answers, setAnswers] = useState<{ id: number; known: boolean }[]>([]);
  const [result, setResult] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const list = words.data ?? [];
  const finished = list.length > 0 && answers.length === list.length;

  useEffect(() => {
    if (!finished || result !== null) return;
    submitPlacement(l, r, answers)
      .then(setResult)
      .catch(() => setFailed(true));
  }, [finished, result, l, r, answers]);

  const answer = (known: boolean) => {
    if (finished) return;
    setAnswers((previous) => [
      ...previous,
      { id: list[previous.length].id, known },
    ]);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight" || event.key.toLowerCase() === "y")
        answer(true);
      if (event.key === "ArrowLeft" || event.key.toLowerCase() === "n")
        answer(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  });

  if (words.isPending) {
    return (
      <div className="card flex w-full flex-col items-center gap-5 rounded-3xl p-7">
        <span className="text-sm text-secondary-text">
          {strings.placement_loading}
        </span>
        <SkeletonBlock className="my-4 h-10 w-44" />
        <div className="grid w-full max-w-sm grid-cols-2 gap-3">
          <SkeletonBlock className="h-12 rounded-xl" />
          <SkeletonBlock className="h-12 rounded-xl" />
        </div>
      </div>
    );
  }
  if (words.isError || failed) {
    return <p className="text-error">{strings.practice_error}</p>;
  }
  if (finished) {
    return (
      <div className="card flex w-full flex-col items-center gap-3 rounded-3xl px-6 py-9 text-center">
        {result === null ? (
          <p className="text-secondary-text">{strings.practice_loading}</p>
        ) : (
          <>
            <Pup shape="circle" mood="happy" className="h-20 w-20" />
            <h2 className="mt-2 font-literata text-2xl font-bold text-main-text">
              {strings.placement_done_title}
            </h2>
            <p className="max-w-md text-main-text">
              {strings.placement_done_text.replace("{level}", result)}
            </p>
            <button
              type="button"
              onClick={onDone}
              className="btn-primary mt-3 px-6 py-3"
            >
              {strings.placement_continue}
            </button>
          </>
        )}
      </div>
    );
  }

  const word = list[answers.length];
  return (
    <div className="card flex w-full flex-col items-center gap-5 rounded-3xl p-6 sm:p-7">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-cream-dark">
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-300"
          style={{ width: `${(100 * answers.length) / list.length}%` }}
        />
      </div>
      <p className="text-sm text-secondary-text">
        {strings.placement_question}
      </p>
      <p
        key={answers.length}
        lang={r}
        className="animate-pop-in py-5 font-literata text-[36px] font-bold leading-tight text-main-text"
      >
        {word.word}
      </p>
      <div className="grid w-full max-w-sm grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => answer(false)}
          className="btn-secondary py-3.5"
        >
          {strings.placement_unknown}
        </button>
        <button
          type="button"
          onClick={() => answer(true)}
          className="btn-primary py-3.5"
        >
          {strings.placement_known}
        </button>
      </div>
      <p className="hidden sm:block text-xs text-secondary-text">
        {strings.placement_keyboard_help}
      </p>
    </div>
  );
}
